import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { finalize, forkJoin } from 'rxjs';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { LogisticsProjectSummary } from '../../logistics-projects/models/logistics-project.model';
import { LogisticsProjectLookupComponent } from '../../logistics-projects/logistics-project-lookup.component';
import { LogisticsProjectsService } from '../../logistics-projects/logistics-projects.service';
import { ContainerTrackingService } from '../container-tracking.service';
import { ContainerCase } from '../models/container-case.model';
import { ContainerDashboardSummary } from '../models/container-dashboard-summary.model';

interface CaseWorkspaceFilters {
  caseNumber: string;
  containerNumber: string;
  billOfLadingNumber: string;
  logisticsProjectId: number | null;
  status: string | null;
  currentState: string | null;
  openedFrom: Date | null;
  openedTo: Date | null;
}

type CaseDirectoryView = 'CASES' | 'BOOKING' | 'BILL_OF_LADING';

interface CaseReferenceGroup {
  key: string;
  reference: string;
  cases: ContainerCase[];
  activeCaseCount: number;
  holdCaseCount: number;
  projectCount: number;
  latestOpenedAt?: Date | string;
}

@Component({
  selector: 'app-container-tracking-overview',
  standalone: true,
  imports: [
    LogisticsProjectLookupComponent,
    CommonModule,
    FormsModule,
    BreadcrumbModule,
    ButtonModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TooltipModule,
    RippleModule,
    PageDataPanelComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageShellComponent
  ],
  templateUrl: './container-tracking-overview.component.html',
  styleUrls: ['./container-tracking-overview.component.css']
})
export class ContainerTrackingOverviewComponent implements OnInit {
  canViewCases = false;
  canCreateCase = false;
  isLoading = false;
  companyId = 0;

  breadcrumbItems: MenuItem[] = [
    { label: 'Container Tracking' },
    { label: 'Cases' }
  ];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  filters: CaseWorkspaceFilters = {
    caseNumber: '',
    containerNumber: '',
    billOfLadingNumber: '',
    logisticsProjectId: null,
    status: null,
    currentState: null,
    openedFrom: null,
    openedTo: null
  };

  statusOptions = [
    { label: 'Open', value: 'OPEN' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'On Hold', value: 'ON_HOLD' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Cancelled', value: 'CANCELLED' }
  ];

  summary: ContainerDashboardSummary = {
    owningCompanyId: 0,
    openCases: 0,
    inProgressCases: 0,
    onHoldCases: 0,
    completedLast7Days: 0,
    activeTransportLegs: 0,
    activeStorageLegs: 0,
    activeExceptions: 0
  };
  cases: ContainerCase[] = [];
  filteredCases: ContainerCase[] = [];
  logisticsProjectOptions: LogisticsProjectSummary[] = [];
  private projectNamesRequest = 0;
  directoryView: CaseDirectoryView = 'CASES';

  constructor(
    private containerTrackingService: ContainerTrackingService,
    private logisticsProjectsService: LogisticsProjectsService,
    private permissionService: PermissionService,
    private sessionStorageService: SessionStorageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.canViewCases = this.permissionService.hasPermission(app_permissions.container_tracking.cases.view);
    this.canCreateCase = this.permissionService.hasPermission(app_permissions.container_tracking.cases.create);
    this.companyId = this.sessionStorageService.getCompany()?.id || 0;
    if (!this.canViewCases || !this.companyId) {
      return;
    }
    this.loadWorkspace();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Open Cases', value: this.summary.openCases, tone: 'info' },
      { label: 'In Progress', value: this.summary.inProgressCases, tone: 'warn' },
      { label: 'On Hold', value: this.summary.onHoldCases, tone: 'danger' },
      { label: 'Active Transport', value: this.summary.activeTransportLegs, tone: 'info' },
      { label: 'Active Storage', value: this.summary.activeStorageLegs, tone: 'warn' },
      { label: 'Exceptions', value: this.summary.activeExceptions, tone: 'danger' }
    ];
  }

  get activeFilterCount(): number {
    return Object.values(this.filters).filter(value => value !== null && value !== '').length;
  }

  get caseReferenceGroups(): CaseReferenceGroup[] {
    if (this.directoryView === 'CASES') {
      return [];
    }
    const groups = new Map<string, CaseReferenceGroup>();
    const isBooking = this.directoryView === 'BOOKING';
    for (const caseItem of this.filteredCases) {
      const rawReference = isBooking ? caseItem.bookingNumber : caseItem.billOfLadingNumber;
      const reference = rawReference?.trim() || (isBooking ? 'Booking not captured' : 'Bill of lading not captured');
      const key = `${isBooking ? 'booking' : 'bl'}:${reference.toLowerCase()}`;
      const existing = groups.get(key) || {
        key,
        reference,
        cases: [],
        activeCaseCount: 0,
        holdCaseCount: 0,
        projectCount: 0,
        latestOpenedAt: undefined
      };
      existing.cases.push(caseItem);
      if (caseItem.activeTransportLegId || caseItem.activeStorageLegId) {
        existing.activeCaseCount += 1;
      }
      if ((caseItem.status || '').toUpperCase() === 'ON_HOLD') {
        existing.holdCaseCount += 1;
      }
      const openedAt = caseItem.openedAt ? new Date(caseItem.openedAt) : undefined;
      const latestOpenedAt = existing.latestOpenedAt ? new Date(existing.latestOpenedAt) : undefined;
      if (openedAt && (!latestOpenedAt || openedAt > latestOpenedAt)) {
        existing.latestOpenedAt = caseItem.openedAt;
      }
      groups.set(key, existing);
    }

    groups.forEach(group => {
      group.projectCount = new Set(group.cases.map(caseItem => caseItem.logisticsProjectId).filter(Boolean)).size;
      group.cases.sort((left, right) => {
        const leftOpened = left.openedAt ? new Date(left.openedAt).getTime() : 0;
        const rightOpened = right.openedAt ? new Date(right.openedAt).getTime() : 0;
        return rightOpened - leftOpened;
      });
    });
    return Array.from(groups.values()).sort((left, right) => {
      const leftOpened = left.latestOpenedAt ? new Date(left.latestOpenedAt).getTime() : 0;
      const rightOpened = right.latestOpenedAt ? new Date(right.latestOpenedAt).getTime() : 0;
      return rightOpened - leftOpened || left.reference.localeCompare(right.reference);
    });
  }

  directoryViewLabel(): string {
    switch (this.directoryView) {
      case 'BOOKING': return 'Grouped by Booking';
      case 'BILL_OF_LADING': return 'Grouped by Bill of Lading';
      default: return 'Case Directory';
    }
  }

  setDirectoryView(view: CaseDirectoryView): void {
    this.directoryView = view;
  }

  loadWorkspace(): void {
    if (!this.companyId) {
      return;
    }
    this.isLoading = true;
    const params = this.buildCaseParams();
    forkJoin({
      summary: this.containerTrackingService.getDashboardSummary(this.companyId),
      cases: this.containerTrackingService.findCases(params)
    }).pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: response => {
          this.summary = response.summary;
          this.cases = response.cases.content || [];
          this.loadPageProjectNames(this.cases);
          this.applyClientSideFilters();
        },
        error: () => {
          this.summary = {
            owningCompanyId: this.companyId,
            openCases: 0,
            inProgressCases: 0,
            onHoldCases: 0,
            completedLast7Days: 0,
            activeTransportLegs: 0,
            activeStorageLegs: 0,
            activeExceptions: 0
          };
          this.cases = [];
          this.filteredCases = [];
        }
      });
  }

  applyFilters(): void {
    this.loadWorkspace();
  }

  clearFilters(): void {
    this.filters = {
      caseNumber: '',
      containerNumber: '',
      billOfLadingNumber: '',
      logisticsProjectId: null,
      status: null,
      currentState: null,
      openedFrom: null,
      openedTo: null
    };
    this.loadWorkspace();
  }

  createCase(): void {
    this.router.navigate(['/container-tracking/cases/add'], {
      queryParams: this.filters.logisticsProjectId ? { projectId: this.filters.logisticsProjectId } : undefined
    });
  }

  openProject(projectId?: number | null): void {
    if (!projectId) {
      return;
    }
    this.router.navigate(['/logistics-projects', projectId]);
  }

  openCase(caseId?: number): void {
    if (!caseId) {
      return;
    }
    this.router.navigate(['/container-tracking/cases/view', caseId]);
  }

  hasReferenceWorkspace(group: CaseReferenceGroup): boolean {
    return !!group.reference && !/not captured/i.test(group.reference);
  }

  openReferenceWorkspace(group: CaseReferenceGroup): void {
    if (!this.hasReferenceWorkspace(group)) {
      return;
    }
    this.router.navigate(['/container-tracking/bookings/view'], {
      queryParams: {
        referenceType: this.directoryView === 'BOOKING' ? 'BOOKING' : 'BILL_OF_LADING',
        referenceNumber: group.reference
      }
    });
  }

  statusSeverity(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'COMPLETED': return 'success';
      case 'ON_HOLD': return 'danger';
      case 'IN_PROGRESS': return 'warn';
      case 'OPEN': return 'info';
      case 'CANCELLED': return 'secondary';
      default: return 'secondary';
    }
  }

  exceptionSeverity(severity?: string): string {
    switch ((severity || '').toLowerCase()) {
      case 'danger': return 'danger';
      case 'warn': return 'warn';
      case 'success': return 'success';
      case 'info': return 'info';
      default: return 'secondary';
    }
  }

  activeLegLabel(caseItem: ContainerCase): string {
    if (caseItem.activeTransportLegId) {
      return 'Transport active';
    }
    if (caseItem.activeStorageLegId) {
      return 'Storage active';
    }
    return 'None';
  }

  projectSummary(caseItem: ContainerCase): LogisticsProjectSummary | undefined {
    return caseItem.logisticsProjectId
      ? this.logisticsProjectOptions.find(project => project.projectId === caseItem.logisticsProjectId)
      : undefined;
  }

  projectLabel(caseItem: ContainerCase): string {
    const project = this.projectSummary(caseItem);
    return project?.projectName || project?.projectNumber || 'Unassigned';
  }

  projectSubtitle(caseItem: ContainerCase): string {
    const project = this.projectSummary(caseItem);
    return [project?.projectNumber, project?.customerCompanyName].filter(Boolean).join(' | ') || 'No linked project';
  }

  projectCustomerLabel(caseItem: ContainerCase): string {
    const project = this.projectSummary(caseItem);
    if (project?.customerCompanyName) {
      return project.customerCompanyName;
    }
    return caseItem.customerCompanyId || caseItem.crmClientId
      ? 'Customer name unavailable'
      : 'No customer captured';
  }

  projectReferenceLabel(caseItem: ContainerCase): string {
    return this.projectSummary(caseItem)?.projectNumber || 'No project reference';
  }

  formatLifecycleLabel(value?: string | null): string {
    if (!value) {
      return 'Pending';
    }
    return value
      .toLowerCase()
      .split('_')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  lifecycleProgressCaption(caseItem: ContainerCase): string {
    if (this.hasJourneyProgress(caseItem)) {
      const completed = caseItem.journeyCompletedStepCount || 0;
      const total = caseItem.journeyStepCount || 0;
      const pauseSuffix = (caseItem.status || '').toUpperCase() === 'ON_HOLD' ? ' | on hold' : '';
      return `${completed} of ${total} required steps completed${pauseSuffix}`;
    }
    const status = (caseItem.status || '').toUpperCase();
    if (status === 'CANCELLED') {
      return 'Lifecycle cancelled';
    }
    if (status === 'ON_HOLD') {
      return `Paused at ${this.formatLifecycleLabel(caseItem.currentState)}`;
    }
    if (status === 'COMPLETED') {
      return 'Container lifecycle completed';
    }
    return `Current milestone: ${this.formatLifecycleLabel(caseItem.currentState || 'Case opened')}`;
  }

  hasJourneyProgress(caseItem: ContainerCase): boolean {
    return (caseItem.journeyStepCount || 0) > 0;
  }

  journeyProgressPercent(caseItem: ContainerCase): number {
    const percentage = Number(caseItem.journeyProgressPercent || 0);
    return Math.min(100, Math.max(0, Number.isFinite(percentage) ? percentage : 0));
  }

  journeyCurrentStepLabel(caseItem: ContainerCase): string {
    if (caseItem.journeyCurrentStepName) {
      return caseItem.journeyCurrentStepName;
    }
    return this.formatLifecycleLabel(caseItem.currentState || 'Case opened');
  }

  currentControlLabel(caseItem: ContainerCase): string {
    if (caseItem.activeTransportLegId && caseItem.activeStorageLegId) {
      return 'Transport-storage handoff in progress';
    }
    if (caseItem.activeTransportLegId) {
      return 'Transport execution currently active';
    }
    if (caseItem.activeStorageLegId) {
      return 'Storage or terminal control currently active';
    }
    if ((caseItem.status || '').toUpperCase() === 'COMPLETED') {
      return 'Lifecycle execution completed';
    }
    return 'Journey planning or execution reconciliation';
  }

  lifecycleMeta(caseItem: ContainerCase): string {
    return [caseItem.currentState, this.activeLegLabel(caseItem)].filter(value => !!value && value !== 'None').join(' | ') || 'No active lifecycle leg';
  }

  private buildCaseParams(): HttpParams {
    let params = new HttpParams()
      .set('owningCompanyId', String(this.companyId))
      .set('size', '100')
      .set('sort', 'openedAt,desc');

    if (this.filters.caseNumber.trim()) {
      params = params.set('caseNumber', this.filters.caseNumber.trim());
    }
    if (this.filters.containerNumber.trim()) {
      params = params.set('containerNumber', this.filters.containerNumber.trim());
    }
    if (this.filters.billOfLadingNumber.trim()) {
      params = params.set('billOfLadingNumber', this.filters.billOfLadingNumber.trim());
    }
    if (this.filters.logisticsProjectId) {
      params = params.set('logisticsProjectId', String(this.filters.logisticsProjectId));
    }
    if (this.filters.status) {
      params = params.set('status', this.filters.status);
    }
    if (this.filters.currentState?.trim()) {
      params = params.set('currentState', this.filters.currentState.trim());
    }
    return params;
  }

  private applyClientSideFilters(): void {
    const openedFrom = this.filters.openedFrom ? this.startOfDay(this.filters.openedFrom) : null;
    const openedTo = this.filters.openedTo ? this.endOfDay(this.filters.openedTo) : null;
    this.filteredCases = this.cases.filter(caseItem => {
      const openedAt = caseItem.openedAt ? new Date(caseItem.openedAt) : null;
      if (openedFrom && (!openedAt || openedAt < openedFrom)) {
        return false;
      }
      if (openedTo && (!openedAt || openedAt > openedTo)) {
        return false;
      }
      return true;
    });
  }

  private startOfDay(value: Date): Date {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private endOfDay(value: Date): Date {
    const date = new Date(value);
    date.setHours(23, 59, 59, 999);
    return date;
  }

  private loadPageProjectNames(cases: ContainerCase[]): void {
    const request = ++this.projectNamesRequest;
    this.logisticsProjectOptions = [];
    const ids = [...new Set(cases.map(item => item.logisticsProjectId).filter((id): id is number => !!id))];
    if (!ids.length) return;
    this.logisticsProjectsService.lookupProjects(this.companyId, '', ids).subscribe({
      next: projects => { if (request === this.projectNamesRequest) this.logisticsProjectOptions = projects; },
      error: () => { if (request === this.projectNamesRequest) this.logisticsProjectOptions = []; }
    });
  }
}
